/**
 * Branded proposal templates — the "cool ones". Three visually distinct,
 * print-ready (A4) proposal designs the operator picks between when
 * creating a proposal:
 *
 *   - `aurora`    — full-bleed magenta/violet gradient cover with a glow,
 *                   glass value chip, gradient section rules. The flagship.
 *   - `onyx`      — matte-black editorial cover with a magenta keyline and
 *                   oversized numbered sections ("01 / Executive summary").
 *   - `blueprint` — light engineering-paper cover with an electric-blue
 *                   spine, grid motif, and mono technical headings.
 *
 * Everything is PURE and DOM-free (records in → HTML string out) so the
 * renders are trivially testable. All layout is inline-styled so the
 * document survives copy/paste + email clients; the single <style> block
 * carries only @page sizing and @media print rules. Money math is shared
 * with `proposalDoc` (computeProposalTotals / lineItemAmount) so branded
 * templates can never disagree with the plain renderer about a figure.
 *
 * `renderDocumentHtml` is the router the UI calls: contracts and
 * proposals with no/unknown branded templateId fall through to the
 * existing `renderProposalHtml`, so nothing regresses.
 */

import type { BusinessDoc } from '../db/types';
import { C } from '../tokens';
import { WORDMARK_WHITE } from '../docs/brandDoc';
import type { ProposalBodySections } from '../docs/proposalBody';
import {
  computeProposalTotals,
  lineItemAmount,
  renderProposalHtml,
  type ProposalLineItem,
  type RenderProposalOptions,
} from './proposalDoc';

// ===========================================================================
// Template registry
// ===========================================================================

export type ProposalTemplateId = 'aurora' | 'onyx' | 'blueprint';

export interface ProposalTemplate {
  id: ProposalTemplateId;
  /** Display name shown on the picker card. */
  name: string;
  /** One-line pitch shown under the name in the picker. */
  description: string;
  /** Primary brand accent for this template (hex). */
  accent: string;
  /** Secondary accent used in gradients / rules (hex). */
  accent2: string;
}

export const PROPOSAL_TEMPLATES: ProposalTemplate[] = [
  {
    id: 'aurora',
    name: 'Aurora',
    description: 'Full-bleed magenta gradient cover with a glass value chip.',
    accent: C.magenta,
    accent2: '#7C3AED',
  },
  {
    id: 'onyx',
    name: 'Onyx',
    description: 'Matte-black editorial cover, numbered sections, keyline accents.',
    accent: C.magentaSoft,
    accent2: C.magentaDeep,
  },
  {
    id: 'blueprint',
    name: 'Blueprint',
    description: 'Engineering-paper cover with an electric-blue spine and grid.',
    accent: C.blueElectric,
    accent2: '#00C2FF',
  },
];

export const DEFAULT_PROPOSAL_TEMPLATE_ID: ProposalTemplateId = 'aurora';

/** Look up a branded template; undefined for legacy/unknown ids. */
export function getProposalTemplate(
  id?: string | null,
): ProposalTemplate | undefined {
  if (!id) return undefined;
  return PROPOSAL_TEMPLATES.find((t) => t.id === id);
}

// ===========================================================================
// Shared rendering primitives (pure, string-only)
// ===========================================================================

const SANS = "'Manrope','Inter',system-ui,-apple-system,'Segoe UI',sans-serif";
const MONO = "'Geist Mono',ui-monospace,SFMono-Regular,Menlo,monospace";
const INK = C.ink950;
const LINE = '#e6e7ee';
const BODY_TEXT = '#3c3f4c';
const MUTED = '#7a7f92';

function esc(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function usd(n: number): string {
  const rounded = Math.round(n * 100) / 100;
  return rounded.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: Number.isInteger(rounded) ? 0 : 2,
    maximumFractionDigits: 2,
  });
}

function fmtDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

/** The white wordmark recolored for light covers. */
function inkLogo(): string {
  return WORDMARK_WHITE.replace(/fill="#fff"/g, `fill="${INK}"`);
}

const label = (color: string) =>
  `font-family:${MONO};font-size:10px;letter-spacing:.14em;text-transform:uppercase;color:${color};`;

/** How a template draws its section headings. */
type HeadingStyle = 'gradient-rule' | 'numbered' | 'mono-spine';

interface Theme {
  template: ProposalTemplate;
  headingStyle: HeadingStyle;
}

function sectionHeading(theme: Theme, index: number, text: string): string {
  const { accent, accent2 } = theme.template;
  if (theme.headingStyle === 'numbered') {
    const no = String(index).padStart(2, '0');
    return (
      `<div style="display:flex;align-items:baseline;gap:12px;margin:0 0 14px;">` +
      `<span style="font-family:${MONO};font-size:13px;font-weight:700;color:${accent};">${no} /</span>` +
      `<h2 style="margin:0;font-size:20px;font-weight:800;letter-spacing:.06em;text-transform:uppercase;color:${INK};">${text}</h2>` +
      '</div>'
    );
  }
  if (theme.headingStyle === 'mono-spine') {
    return (
      `<h2 style="margin:0 0 14px;padding-left:12px;border-left:4px solid ${accent};font-family:${MONO};font-size:17px;font-weight:700;letter-spacing:.08em;text-transform:uppercase;color:${INK};">${text}</h2>`
    );
  }
  return (
    `<div style="height:4px;width:52px;border-radius:2px;background:linear-gradient(90deg,${accent},${accent2});margin:0 0 12px;"></div>` +
    `<h2 style="margin:0 0 14px;font-size:24px;font-weight:800;letter-spacing:-0.01em;color:${INK};">${text}</h2>`
  );
}

// --- Pricing table ----------------------------------------------------------

const td = (extra = '') =>
  `padding:12px 14px;border-bottom:1px solid ${LINE};color:${BODY_TEXT};font-size:13.5px;${extra}`;
const NUM = 'text-align:right;font-variant-numeric:tabular-nums;';

function pricingTable(items: ProposalLineItem[], theme: Theme): string {
  const { accent } = theme.template;
  const th = (extra = '') =>
    `text-align:left;padding:10px 14px;border-bottom:2px solid ${accent};${label(MUTED)}${extra}`;

  const rows = items
    .map((item) => {
      const cadence = item.cadence === 'recurring' ? 'Recurring' : 'One-time';
      const discount = Math.min(100, Math.max(0, item.discountPct ?? 0));
      const desc =
        esc(item.description) +
        (discount > 0
          ? ` <span style="${label(accent)}">−${discount}%</span>`
          : '');
      return (
        '<tr>' +
        `<td style="${td()}">${desc}</td>` +
        `<td style="${td(NUM)}">${esc(String(item.qty))}</td>` +
        `<td style="${td()}">${esc(item.unit ?? '—')}</td>` +
        `<td style="${td()}">${cadence}</td>` +
        `<td style="${td(NUM)}font-weight:600;color:${INK};">${usd(lineItemAmount(item))}</td>` +
        '</tr>'
      );
    })
    .join('');

  const totals = computeProposalTotals(items);
  const totalRow = (name: string, amount: number, emphasis = false): string =>
    '<tr>' +
    `<td colspan="4" style="${td(`text-align:right;${emphasis ? `font-weight:800;color:#fff;background:${accent};border-bottom:none;` : `color:${MUTED};`}`)}">${name}</td>` +
    `<td style="${td(`${NUM}${emphasis ? `font-weight:800;color:#fff;background:${accent};border-bottom:none;` : `font-weight:600;color:${INK};`}`)}">${usd(amount)}</td>` +
    '</tr>';

  const empty =
    items.length === 0
      ? `<tr><td colspan="5" style="${td(`color:${MUTED};font-style:italic;`)}">No line items yet — pricing to be finalized with the client.</td></tr>`
      : '';

  return (
    `<table style="width:100%;border-collapse:collapse;margin:4px 0 0;">` +
    '<thead><tr>' +
    `<th style="${th()}">Description</th>` +
    `<th style="${th(NUM)}">Qty</th>` +
    `<th style="${th()}">Unit</th>` +
    `<th style="${th()}">Cadence</th>` +
    `<th style="${th(NUM)}">Amount</th>` +
    '</tr></thead>' +
    '<tbody>' +
    empty +
    rows +
    totalRow('One-time subtotal', totals.oneTimeSubtotal) +
    totalRow('Recurring subtotal', totals.recurringSubtotal) +
    (totals.discountTotal > 0
      ? totalRow('Discount applied', -totals.discountTotal)
      : '') +
    totalRow('Total', totals.total, true) +
    '</tbody></table>'
  );
}

// --- Signature block ---------------------------------------------------------

function signatureColumn(party: string, signer: string, title?: string): string {
  const line = (name: string) =>
    `<div style="margin-top:26px;border-bottom:1.5px solid ${INK};height:22px;"></div>` +
    `<div style="margin-top:6px;${label(MUTED)}">${name}</div>`;
  return (
    `<div style="flex:1;min-width:220px;">` +
    `<div style="${label(MUTED)}">For</div>` +
    `<div style="margin-top:4px;font-size:15px;font-weight:800;color:${INK};">${esc(party)}</div>` +
    `<div style="margin-top:2px;font-size:12.5px;color:${MUTED};">${esc(signer)}${title ? ` · ${esc(title)}` : ''}</div>` +
    line('Signature') +
    line('Name') +
    line('Date') +
    '</div>'
  );
}

// --- Shell --------------------------------------------------------------------

function shell(args: {
  templateId: ProposalTemplateId;
  title: string;
  accent: string;
  cover: string;
  main: string;
  footer: string;
}): string {
  const { templateId, title, accent, cover, main, footer } = args;
  return (
    '<!DOCTYPE html>' +
    `<html lang="en"><head><meta charset="utf-8"/><meta name="viewport" content="width=device-width,initial-scale=1"/>` +
    `<title>${esc(title)}</title>` +
    '<style>' +
    '@page{size:A4;margin:0;}' +
    '@media print{' +
    '.no-print{display:none !important;}' +
    'body{margin:0 !important;background:#fff !important;}' +
    '.pd-page{max-width:none !important;margin:0 !important;box-shadow:none !important;border-radius:0 !important;}' +
    '.pd-cover{page-break-after:always;-webkit-print-color-adjust:exact;print-color-adjust:exact;}' +
    'header,footer,table,section{-webkit-print-color-adjust:exact;print-color-adjust:exact;}' +
    'section,table{page-break-inside:avoid;}' +
    '}' +
    '</style></head>' +
    `<body data-template="${templateId}" style="margin:0;background:#101017;font-family:${SANS};color:${BODY_TEXT};-webkit-font-smoothing:antialiased;">` +
    `<button class="no-print" onclick="window.print()" style="position:fixed;top:16px;right:16px;z-index:10;padding:10px 18px;border:none;border-radius:999px;background:${accent};color:#fff;font-family:${SANS};font-size:13px;font-weight:700;cursor:pointer;box-shadow:0 8px 24px rgba(0,0,0,0.35);">Print / Save PDF</button>` +
    `<div class="pd-page" style="max-width:820px;margin:0 auto;background:#ffffff;box-shadow:0 24px 64px rgba(0,0,0,0.45);">` +
    `<div class="pd-cover">${cover}</div>` +
    `<main style="padding:56px 64px;">${main}</main>` +
    footer +
    '</div>' +
    '</body></html>'
  );
}

// ===========================================================================
// Covers — the part that makes each template unmistakably itself
// ===========================================================================

interface CoverContext {
  doc: BusinessDoc;
  clientName: string;
  owner: string;
  brandName: string;
  date: string;
  total: number;
}

function coverMeta(ctx: CoverContext, color: string): string {
  const cell = (k: string, v: string) =>
    `<div><div style="${label(color)}">${k}</div><div style="margin-top:5px;font-size:14px;font-weight:600;">${esc(v)}</div></div>`;
  return (
    `<div style="display:flex;flex-wrap:wrap;gap:36px;margin-top:40px;">` +
    cell('Prepared for', ctx.clientName) +
    cell('Prepared by', `${ctx.owner} · ${ctx.brandName}`) +
    cell('Date', ctx.date) +
    (ctx.doc.validUntil ? cell('Valid until', fmtDate(ctx.doc.validUntil)) : '') +
    '</div>'
  );
}

function auroraCover(ctx: CoverContext, t: ProposalTemplate): string {
  return (
    `<header style="position:relative;overflow:hidden;color:#fff;padding:72px 64px 60px;background:linear-gradient(140deg,#12041c 0%,#3b0764 42%,${t.accent2} 74%,${t.accent} 100%);">` +
    `<div style="position:absolute;top:-140px;right:-120px;width:420px;height:420px;border-radius:50%;background:radial-gradient(circle,rgba(255,0,255,0.45),rgba(255,0,255,0));"></div>` +
    `<div style="position:absolute;bottom:-160px;left:-100px;width:380px;height:380px;border-radius:50%;background:radial-gradient(circle,rgba(124,58,237,0.5),rgba(124,58,237,0));"></div>` +
    `<div style="position:relative;">` +
    `<div style="height:18px;">${WORDMARK_WHITE}</div>` +
    `<p style="margin:40px 0 12px;${label('rgba(255,255,255,0.75)')}font-size:12px;">Proposal · ${esc(ctx.date)}</p>` +
    `<h1 style="margin:0 0 18px;font-size:44px;line-height:1.06;font-weight:800;letter-spacing:-0.02em;max-width:17ch;">${esc(ctx.doc.title)}</h1>` +
    `<p style="margin:0;font-size:16px;line-height:1.55;color:rgba(255,255,255,0.78);max-width:56ch;">Prepared for ${esc(ctx.clientName)} by ${esc(ctx.brandName)}.</p>` +
    coverMeta(ctx, 'rgba(255,255,255,0.5)') +
    `<div style="display:inline-flex;align-items:baseline;gap:12px;margin-top:38px;padding:16px 26px;border:1px solid rgba(255,255,255,0.28);border-radius:16px;background:rgba(255,255,255,0.12);backdrop-filter:blur(6px);">` +
    `<span style="${label('rgba(255,255,255,0.7)')}">Engagement total</span>` +
    `<span style="font-size:30px;font-weight:800;color:#fff;">${usd(ctx.total)}</span>` +
    '</div>' +
    '</div></header>'
  );
}

function onyxCover(ctx: CoverContext, t: ProposalTemplate): string {
  return (
    `<header style="position:relative;background:#0b0b0f;color:#fff;padding:0;">` +
    `<div style="height:6px;background:linear-gradient(90deg,${t.accent},${t.accent2});"></div>` +
    `<div style="padding:70px 64px 58px;">` +
    `<div style="display:flex;align-items:center;justify-content:space-between;gap:20px;">` +
    `<div style="height:18px;">${WORDMARK_WHITE}</div>` +
    `<span style="${label(t.accent)}font-size:11px;">Proposal — ${esc(ctx.date)}</span>` +
    '</div>' +
    `<div style="margin:64px 0 0;border-top:1px solid rgba(255,255,255,0.14);padding-top:28px;">` +
    `<h1 style="margin:0 0 20px;font-size:46px;line-height:1.05;font-weight:800;letter-spacing:-0.025em;max-width:16ch;">${esc(ctx.doc.title)}</h1>` +
    `<p style="margin:0;font-family:${MONO};font-size:13px;line-height:1.7;color:rgba(255,255,255,0.6);max-width:60ch;">Prepared for ${esc(ctx.clientName)} · by ${esc(ctx.brandName)}</p>` +
    '</div>' +
    coverMeta(ctx, 'rgba(255,255,255,0.4)') +
    `<div style="display:flex;align-items:baseline;gap:14px;margin-top:40px;padding-top:22px;border-top:1px solid rgba(255,255,255,0.14);">` +
    `<span style="${label('rgba(255,255,255,0.5)')}">Engagement total</span>` +
    `<span style="font-size:32px;font-weight:800;color:${t.accent};">${usd(ctx.total)}</span>` +
    '</div>' +
    '</div></header>'
  );
}

function blueprintCover(ctx: CoverContext, t: ProposalTemplate): string {
  const grid =
    'background-image:linear-gradient(rgba(0,130,243,0.10) 1px,transparent 1px),linear-gradient(90deg,rgba(0,130,243,0.10) 1px,transparent 1px);background-size:28px 28px;';
  return (
    `<header style="position:relative;display:flex;background:#f4f6fb;color:${INK};">` +
    `<div style="width:14px;flex:none;background:linear-gradient(180deg,${t.accent},${t.accent2});"></div>` +
    `<div style="flex:1;padding:64px 56px 52px;${grid}">` +
    `<div style="height:18px;">${inkLogo()}</div>` +
    `<p style="margin:40px 0 12px;${label(t.accent)}font-size:12px;">Proposal · Spec № ${esc(ctx.doc.id.slice(0, 8))} · ${esc(ctx.date)}</p>` +
    `<h1 style="margin:0 0 16px;font-size:40px;line-height:1.08;font-weight:800;letter-spacing:-0.02em;color:${INK};max-width:18ch;">${esc(ctx.doc.title)}</h1>` +
    `<p style="margin:0;font-family:${MONO};font-size:13px;line-height:1.7;color:${MUTED};max-width:58ch;">Prepared for ${esc(ctx.clientName)} by ${esc(ctx.brandName)}.</p>` +
    coverMeta(ctx, MUTED) +
    `<div style="display:inline-flex;align-items:baseline;gap:12px;margin-top:36px;padding:14px 22px;border:1.5px solid ${t.accent};border-radius:10px;background:#fff;">` +
    `<span style="${label(t.accent)}">Engagement total</span>` +
    `<span style="font-size:28px;font-weight:800;color:${t.accent};">${usd(ctx.total)}</span>` +
    '</div>' +
    '</div></header>'
  );
}

// ===========================================================================
// renderBrandedProposalHtml
// ===========================================================================

export interface RenderBrandedProposalOptions extends RenderProposalOptions {
  /** Which branded template to use; defaults to the flagship (aurora). */
  templateId?: string;
  /**
   * Optional Brain-grounded prose sections (see docs/proposalBody). When
   * present they replace/extend the generic prose: executiveSummary swaps
   * in for the boilerplate summary, aboutUs / whyUs / nextSteps render as
   * their own numbered sections, and scopeOfWork's lines drive the scope
   * list. When absent the output is byte-identical to the legacy render,
   * so the existing Preview / Print path is untouched.
   */
  bodySections?: ProposalBodySections;
}

/**
 * Render a proposal with one of the branded templates: cover (logo,
 * brand color, engagement total), executive summary, scope, pricing
 * table, terms, and acceptance/signature block — self-contained,
 * A4/print-ready HTML. Unknown templateIds fall back to the default
 * branded template. Never fabricates figures: everything comes from the
 * doc + supplied line items.
 */
export function renderBrandedProposalHtml(
  doc: BusinessDoc,
  options: RenderBrandedProposalOptions,
): string {
  const template =
    getProposalTemplate(options.templateId) ??
    getProposalTemplate(DEFAULT_PROPOSAL_TEMPLATE_ID)!;
  const theme: Theme = {
    template,
    headingStyle:
      template.id === 'onyx'
        ? 'numbered'
        : template.id === 'blueprint'
          ? 'mono-spine'
          : 'gradient-rule',
  };

  const brandName = options.brand?.name ?? 'Argo IQ';
  const account = options.account ?? null;
  const clientName = account?.name ?? doc.entityName;
  const owner = doc.ownerName ?? account?.ownerName ?? 'The team';
  const items = options.lineItems;
  const totals = computeProposalTotals(items);
  const date = fmtDate(doc.createdAt);
  const ctx: CoverContext = {
    doc,
    clientName,
    owner,
    brandName,
    date,
    total: totals.total,
  };

  const cover =
    template.id === 'onyx'
      ? onyxCover(ctx, template)
      : template.id === 'blueprint'
        ? blueprintCover(ctx, template)
        : auroraCover(ctx, template);

  const section = (index: number, heading: string, body: string): string =>
    `<section style="margin:0 0 44px;">${sectionHeading(theme, index, heading)}${body}</section>`;

  const para = (html: string): string =>
    `<p style="margin:0 0 12px;font-size:14.5px;line-height:1.65;color:${BODY_TEXT};max-width:70ch;">${html}</p>`;

  // --- Brain-grounded prose (optional) -------------------------------------
  // Helpers to render composed section text with the template's typographic
  // hierarchy: paragraphs for prose, accented lists for line-based sections.
  const body = options.bodySections;

  const textParas = (text: string): string =>
    text
      .split(/\n+/)
      .map((l) => l.trim())
      .filter(Boolean)
      .map((l) => para(esc(l)))
      .join('');

  const li = (html: string): string =>
    `<li style="margin:0 0 10px;font-size:14.5px;line-height:1.6;color:${BODY_TEXT};">${html}</li>`;

  /** Bullet list; the lead (before " — ") is bolded like the scope list. */
  const bulletList = (text: string): string =>
    `<ul style="margin:0;padding-left:22px;max-width:70ch;">` +
    text
      .split('\n')
      .map((l) => l.trim())
      .filter(Boolean)
      .map((l) => {
        const dash = l.indexOf(' — ');
        return dash > 0
          ? li(
              `<strong style="color:${INK};">${esc(l.slice(0, dash))}</strong>${esc(l.slice(dash))}`,
            )
          : li(esc(l));
      })
      .join('') +
    '</ul>';

  const orderedList = (text: string): string =>
    `<ol style="margin:0;padding-left:22px;max-width:70ch;">` +
    text
      .split('\n')
      .map((l) => l.trim())
      .filter(Boolean)
      .map((l) => li(esc(l)))
      .join('') +
    '</ol>';

  // Sections are numbered dynamically so optional Brain-grounded sections
  // (About us / Why us / Next steps) slot in without breaking the count.
  // With no bodySections the sequence — and the output — is exactly the
  // legacy render: summary(1) scope(2) pricing(3) terms(4) signatures(5).
  let sectionNo = 0;
  const parts: string[] = [];
  const pushSection = (heading: string, html: string): void => {
    sectionNo += 1;
    parts.push(section(sectionNo, heading, html));
  };

  pushSection(
    'Executive summary',
    body?.executiveSummary
      ? textParas(body.executiveSummary)
      : para(
          `${esc(brandName)} proposes an engagement with ${esc(clientName)} covering ${items.length === 1 ? 'one workstream' : `${items.length || 'the'} workstreams`} detailed below, at a total engagement value of ${usd(totals.total)}${totals.recurringSubtotal > 0 ? ` (${usd(totals.oneTimeSubtotal)} one-time, ${usd(totals.recurringSubtotal)} recurring over the quoted term)` : ''}.`,
        ) +
          para(
            'Scope, pricing, and terms in this document reflect the current draft and remain open to revision until countersigned by both parties.',
          ),
  );

  if (body?.aboutUs) pushSection('About us', textParas(body.aboutUs));

  pushSection(
    'Scope of work',
    body?.scopeOfWork
      ? bulletList(body.scopeOfWork)
      : items.length > 0
        ? `<ul style="margin:0;padding-left:22px;max-width:70ch;">` +
            items
              .map(
                (item) =>
                  `<li style="margin:0 0 10px;font-size:14.5px;line-height:1.6;color:${BODY_TEXT};"><strong style="color:${INK};">${esc(item.description)}</strong> — ${esc(String(item.qty))}${item.unit ? ` ${esc(item.unit)}` : ''}, ${item.cadence === 'recurring' ? 'recurring' : 'one-time'}.</li>`,
              )
              .join('') +
            '</ul>'
        : `<p style="margin:0;font-size:14.5px;line-height:1.6;color:${MUTED};font-style:italic;">Scope to be finalized with ${esc(clientName)}.</p>`,
  );

  if (body?.whyUs) pushSection('Why us', bulletList(body.whyUs));

  pushSection('Pricing', pricingTable(items, theme));

  pushSection(

    'Terms',
    `<ol style="margin:0;padding-left:22px;max-width:70ch;">` +
      [
        doc.validUntil
          ? `This proposal is valid until ${esc(fmtDate(doc.validUntil))}; pricing may be revised thereafter.`
          : 'This proposal is valid for 30 days from the date above unless extended in writing.',
        'One-time fees are invoiced on signature; recurring fees are invoiced at the start of each period.',
        'Payment terms are net 15 from invoice date unless otherwise agreed.',
        'Either party may terminate for material breach with 15 days written notice and opportunity to cure.',
        `All work product and confidential information exchanged under this engagement remains subject to the parties' confidentiality obligations.`,
      ]
        .map(
          (t) =>
            `<li style="margin:0 0 10px;font-size:13.5px;line-height:1.6;color:${BODY_TEXT};">${t}</li>`,
        )
        .join('') +
      '</ol>',
  );

  if (body?.nextSteps) pushSection('Next steps', orderedList(body.nextSteps));

  const signatures =
    `<section style="margin:0;padding:28px;border:1px solid ${LINE};border-radius:14px;background:#f7f7fa;page-break-inside:avoid;">` +
    sectionHeading(theme, sectionNo + 1, 'Acceptance &amp; signatures') +
    `<p style="margin:0 0 8px;font-size:13.5px;line-height:1.6;color:${BODY_TEXT};max-width:70ch;">Signing below indicates acceptance of the scope, pricing, and terms in this proposal.</p>` +
    `<div style="display:flex;flex-wrap:wrap;gap:40px;margin-top:8px;">` +
    signatureColumn(brandName, owner) +
    signatureColumn(
      clientName,
      account?.pocName ?? 'Authorized signatory',
      account?.pocTitle,
    ) +
    '</div>' +
    '</section>';

  const footer =
    `<footer style="background:${template.id === 'blueprint' ? '#f4f6fb' : INK};color:${template.id === 'blueprint' ? MUTED : 'rgba(255,255,255,0.6)'};padding:22px 64px;display:flex;align-items:center;justify-content:space-between;gap:20px;border-top:3px solid ${template.accent};${label(template.id === 'blueprint' ? MUTED : 'rgba(255,255,255,0.6)')}">` +
    `<span style="height:14px;display:inline-flex;">${template.id === 'blueprint' ? inkLogo() : WORDMARK_WHITE}</span>` +
    `<span>${esc(brandName)} · ${esc(doc.title)}</span>` +
    '</footer>';

  return shell({
    templateId: template.id,
    title: doc.title,
    accent: template.accent,
    cover,
    main: parts.join('') + signatures,
    footer,
  });
}

// ===========================================================================
// Router
// ===========================================================================

/**
 * The single entry the UI calls for Preview / Print. Proposals whose
 * `templateId` names a branded template render through it; contracts and
 * legacy/unknown template ids fall through to the existing
 * `renderProposalHtml` (which itself branches proposal vs contract), so
 * every document that rendered before still renders identically.
 */
export function renderDocumentHtml(
  doc: BusinessDoc,
  options: RenderProposalOptions & Pick<RenderBrandedProposalOptions, 'bodySections'>,
): string {
  if (doc.kind === 'proposal' && getProposalTemplate(doc.templateId)) {
    return renderBrandedProposalHtml(doc, {
      ...options,
      templateId: doc.templateId,
    });
  }
  return renderProposalHtml(doc, options);
}
